/**
 * What the manifest claims a lot is — the ground truth its answers are graded
 * against — in the words the manifest itself uses.
 *
 * The scorer keys a brand on a lowercase token ("trafficmaster", "ge"). Shown
 * as ground truth that reads like a typo, so the brand is printed the way the
 * manifest spells it, found among the words of its own product name.
 */
const alnum = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// The end of the leading run of `word` that spells `count` letters and digits.
const prefixEnd = (word, count) => {
  let seen = 0;
  for (let index = 0; index < word.length; index += 1) {
    if (/[A-Za-z0-9]/.test(word[index])) seen += 1;
    if (seen === count) return index + 1;
  }
  return word.length;
};

export const displayManifestBrand = (expectedName, expectedBrand) => {
  const brand = alnum(expectedBrand);
  if (!brand) return '';
  const words = String(expectedName || '')
    .split(/\s+/)
    .map((word) => word.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, ''))
    .filter(Boolean);
  // A word names the brand when it is the brand, or the brand run into a
  // hyphen, an apostrophe or a number — never a longer word ("General" is not
  // GE). The first such word wins: a later "Deluxe Arcade Game" must not stand
  // in for the "Arcade-1UP" the line opens with.
  const namesBrand = (word) => {
    const key = alnum(word);
    if (key === brand) return true;
    return key.startsWith(brand) && /^[-'’0-9]/.test(word.slice(prefixEnd(word, brand.length)));
  };
  const word = words.find(namesBrand);
  if (!word) return String(expectedBrand);
  // "Dyna-Glo", "Arcade-1UP", "Member's" are the brand whole; "GE2.0" is GE and a size.
  const end = prefixEnd(word, brand.length);
  return /^[-'’]/.test(word.slice(end)) ? word : word.slice(0, end);
};

/**
 * A lot that lists several products is judged whole: each product its manifest
 * specifically names, and whether an answer item names it. `judged` is false
 * for a lot the multi-product judge never saw (a single product, an older run),
 * which reads as "not graded"; `pending` while it waits to be judged.
 */
export const getLotProducts = (result = {}) => {
  const lot = result && typeof result === 'object' ? result : {};
  if (!Array.isArray(lot.multi_products)) {
    return { judged: false, pending: lot.multi_pending === true, products: [], found: 0 };
  }
  const products = lot.multi_products
    .filter((product) => product && typeof product === 'object')
    .map((product) => ({
      name: String(product.name || ''),
      brand: String(product.brand || ''),
      found: product.found === true,
      matchedItem: String(product.matched_item || ''),
      brandCorrect: typeof product.brand_correct === 'boolean' ? product.brand_correct : null,
    }));
  return { judged: true, pending: false, products, found: products.filter((p) => p.found).length };
};

const count = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

/**
 * Where one answer item stands in a lot graded as several products: pending
 * while the lot is judged, else the listed product it was matched to (with its
 * brand verdict) or none — an accessory, a hidden-item estimate, or a product
 * the manifest does not list. Null when the lot is not graded that way.
 */
export const getItemMultiMatch = (item = {}, result = {}) => {
  const lot = getLotProducts(result);
  if (lot.pending) return { pending: true, product: '', brandCorrect: null };
  if (!lot.judged || !lot.products.length) return null;
  const row = item && typeof item === 'object' ? item : {};
  return {
    pending: false,
    product: typeof row.multi_product === 'string' ? row.multi_product : '',
    brandCorrect: typeof row.multi_brand_correct === 'boolean' ? row.multi_brand_correct : null,
  };
};

/**
 * The run's one Product number: every product its manifests describe counts
 * once — a single-product lot's product and each product a multi-product lot
 * lists — with the two parts kept for the tooltip.
 */
export const getRunProductOverall = (run = {}) => {
  const stats = run && typeof run.identification === 'object' && run.identification ? run.identification : {};
  const accuracy = stats.product_overall_accuracy;
  return {
    accuracy: typeof accuracy === 'number' && Number.isFinite(accuracy) ? accuracy : null,
    pending: count(stats.product_overall_pending),
    singleCorrect: count(stats.product_correct),
    singleScored: count(stats.product_scored),
    listedFound: count(stats.multi_found),
    listedNamed: count(stats.multi_named),
    listedLots: count(stats.multi_lots),
    wrongClaims: count(stats.extras_wrong),
  };
};

export const describeRunProductOverall = (overall) => {
  const parts = [];
  if (overall.singleScored) parts.push(`single-product lots ${overall.singleCorrect}/${overall.singleScored}`);
  if (overall.listedNamed) {
    parts.push(`listed products ${overall.listedFound}/${overall.listedNamed} in ${overall.listedLots} lot(s)`);
  }
  if (overall.wrongClaims) parts.push(`${overall.wrongClaims} named product(s) not in their lot`);
  if (overall.pending) parts.push(`${overall.pending} lot(s) still being judged — refresh shortly`);
  return parts.join(' · ');
};

/**
 * The run's one Brand number, pooled the same way: single-product lots whose
 * manifest names a maker, each listed product whose manifest names one (found
 * or not), and each wrong claim naming a maker the manifest does not.
 */
export const getRunBrandOverall = (run = {}) => {
  const stats = run && typeof run.identification === 'object' && run.identification ? run.identification : {};
  const accuracy = stats.brand_overall_accuracy;
  return {
    accuracy: typeof accuracy === 'number' && Number.isFinite(accuracy) ? accuracy : null,
    singleCorrect: count(stats.brand_correct),
    singleScored: count(stats.brand_scored),
    listedCorrect: count(stats.multi_brand_correct),
    listedScored: count(stats.multi_brand_scored),
    wrongMakers: count(stats.extras_foreign_brand),
  };
};

export const describeRunBrandOverall = (overall) => {
  const parts = [];
  if (overall.singleScored) parts.push(`single-product lots ${overall.singleCorrect}/${overall.singleScored}`);
  if (overall.listedScored) parts.push(`listed products ${overall.listedCorrect}/${overall.listedScored}`);
  if (overall.wrongMakers) parts.push(`${overall.wrongMakers} maker(s) not in their lot`);
  return parts.join(' · ');
};

const EXTRA_LABELS = {
  unit: 'Extra unit',
  hidden: 'Hidden estimate',
  part: 'Part / accessory',
  vague: 'Too vague',
  other: 'Unlisted',
};

/**
 * What an answer item the lot's products did not match is, as the lot judge
 * read it. `counted` marks a product that is not in the lot at all — a wrong
 * claim, counted against Product (and Brand when its maker is not the lot's).
 * Null for an item the judge matched, or never saw.
 */
export const getItemExtra = (item = {}) => {
  const row = item && typeof item === 'object' ? item : {};
  if (!Object.prototype.hasOwnProperty.call(EXTRA_LABELS, row.extra_kind)) return null;
  const counted = row.extra_counted === true;
  return {
    kind: row.extra_kind,
    counted,
    foreignBrand: row.extra_foreign_brand === true,
    label: counted ? 'Not in lot ✗' : EXTRA_LABELS[row.extra_kind],
  };
};
